/**
 * Super Inteligente - Smart Supermarket
 * Aisle-by-aisle browsing, saved lists, recipes, barcode scanner, savings tracker
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, Plus, Minus, Trash2, X, Zap, Store, Truck, ChevronRight, Package, Heart, BookOpen, Camera, DollarSign, Clock, Users, Save, List } from 'lucide-react';
import { MarketplaceHeader, MarketplaceFooter, MobileBottomNav } from '../../components/marketplace/MarketplaceLayout';
import { Helmet } from 'react-helmet-async';
import api from '../../services/api';
import { toast } from 'sonner';
import SuperProductSheet from '../../components/marketplace/SuperProductSheet';

const SECTION_IMG = '/api/v1/uploads/super_sections';

const SECTIONS_META = {
  abarrotes:       { label: 'Abarrotes',          img: `${SECTION_IMG}/abarrotes.jpg`,       desc: 'Despensa y productos básicos' },
  frutas_verduras: { label: 'Frutas y Verduras',   img: `${SECTION_IMG}/frutas_verduras.jpg`, desc: 'Frescura del día' },
  lacteos:         { label: 'Lácteos',             img: `${SECTION_IMG}/lacteos.jpg`,         desc: 'Leche, queso, yogurt y más' },
  carnes:          { label: 'Carnes',              img: `${SECTION_IMG}/carnes.jpg`,          desc: 'Res, pollo, cerdo y embutidos' },
  bebidas:         { label: 'Bebidas',             img: `${SECTION_IMG}/bebidas.jpg`,         desc: 'Refrescos, aguas y jugos' },
  limpieza:        { label: 'Limpieza',            img: `${SECTION_IMG}/limpieza.jpg`,        desc: 'Hogar limpio y ordenado' },
  higiene:         { label: 'Higiene Personal',    img: `${SECTION_IMG}/higiene.jpg`,         desc: 'Cuidado personal' },
  panaderia:       { label: 'Panadería',           img: `${SECTION_IMG}/abarrotes.jpg`,       desc: 'Pan dulce y artesanal' },
  vinos_licores:   { label: 'Vinos y Licores',      img: `${SECTION_IMG}/bebidas.jpg`,         desc: 'Cervezas, tequila, vino y más' },
  botanas:         { label: 'Botanas',             img: `${SECTION_IMG}/abarrotes.jpg`,       desc: 'Snacks, papas, cacahuates y más' },
  otros:           { label: 'Otros',               img: `${SECTION_IMG}/abarrotes.jpg`,       desc: 'Productos varios' },
};

const SuperInteligente = () => {
  const navigate = useNavigate();
  const [allProducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [cart, setCart] = useState(() => {
    try { const saved = localStorage.getItem('super_cart'); return saved ? JSON.parse(saved) : []; }
    catch { return []; }
  });
  const [showCart, setShowCart] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showRecipes, setShowRecipes] = useState(false);
  const [recipes, setRecipes] = useState([]);
  const [recipeCategories, setRecipeCategories] = useState([]);
  const [recipeStep, setRecipeStep] = useState(0); // 0=personas, 1=antojo, 2=resultados
  const [recipeServings, setRecipeServings] = useState(4);
  const [recipeCategory, setRecipeCategory] = useState(null);
  const [recipesLoading, setRecipesLoading] = useState(false);
  const [showSavedLists, setShowSavedLists] = useState(false);
  const [savedLists, setSavedLists] = useState(() => {
    try { const s = localStorage.getItem('super_saved_lists'); return s ? JSON.parse(s) : []; }
    catch { return []; }
  });
  const [showScanner, setShowScanner] = useState(false);
  const [activeOrderId, setActiveOrderId] = useState(null);
  const sectionRefs = useRef({});
  const navRef = useRef(null);
  const searchRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => { loadData(); checkActiveOrder(); }, []);
  useEffect(() => { localStorage.setItem('super_cart', JSON.stringify(cart)); }, [cart]);

  // Lock body scroll when any modal is open
  useEffect(() => {
    const anyOpen = showCart || showRecipes || showSavedLists || showScanner || !!selectedProduct;
    document.body.style.overflow = anyOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [showCart, showRecipes, showSavedLists, showScanner, selectedProduct]);
  useEffect(() => { localStorage.setItem('super_saved_lists', JSON.stringify(savedLists)); }, [savedLists]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [catRes, prodRes] = await Promise.all([
        api.get('/super-inteligente/categories'),
        api.get('/super-inteligente/products', { params: { limit: 500 } }),
      ]);
      setCategories(catRes.data);
      setAllProducts(prodRes.data.products);
      if (catRes.data.length > 0) setActiveSection(catRes.data[0].category);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // Group products by category
  const productsByCategory = {};
  (searchResults || allProducts).forEach(p => {
    if (!productsByCategory[p.category]) productsByCategory[p.category] = [];
    productsByCategory[p.category].push(p);
  });
  const SECTION_ORDER = ['abarrotes', 'frutas_verduras', 'lacteos', 'carnes', 'desayuno', 'bebidas', 'limpieza', 'higiene', 'panaderia', 'otros', 'botanas', 'vinos_licores'];
  const orderedSections = SECTION_ORDER.filter(cat => productsByCategory[cat]?.length > 0)
    .concat(categories.map(c => c.category).filter(cat => productsByCategory[cat]?.length > 0 && !SECTION_ORDER.includes(cat)));

  // Scroll spy
  useEffect(() => {
    const handleScroll = () => {
      for (const cat of orderedSections) {
        const el = sectionRefs.current[cat];
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 200 && rect.bottom > 200) { setActiveSection(cat); break; }
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [orderedSections]);

  const scrollToSection = (cat) => {
    setActiveSection(cat);
    const el = sectionRefs.current[cat];
    if (el) { window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 170, behavior: 'smooth' }); }
    const navBtn = document.getElementById(`nav-${cat}`);
    if (navBtn) navBtn.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  };

  // Search with autocomplete
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) { setSearchResults(null); setSuggestions([]); return; }
    try {
      const [prodRes, sugRes] = await Promise.all([
        api.get('/super-inteligente/products', { params: { q: searchQuery, limit: 100 } }),
        api.get('/super-inteligente/suggestions', { params: { q: searchQuery } }),
      ]);
      setSearchResults(prodRes.data.products);
      setSuggestions(sugRes.data.suggestions || []);
    } catch (e) { console.error(e); }
  }, [searchQuery]);

  useEffect(() => {
    const t = setTimeout(handleSearch, 300);
    return () => clearTimeout(t);
  }, [searchQuery, handleSearch]);

  const clearSearch = () => { setSearchQuery(''); setSearchResults(null); setSuggestions([]); setShowSuggestions(false); };

  const selectSuggestion = (s) => {
    setSearchQuery(s.name);
    setShowSuggestions(false);
    handleSearch();
  };

  // Cart logic
  const addToCart = (product, qty = null) => {
    const step = product.unit === 'kg' ? 0.5 : 1;
    setCart(prev => {
      const existing = prev.find(i => i.catalog_product_id === product.id);
      if (existing) {
        return prev.map(i => i.catalog_product_id === product.id ? { ...i, quantity: qty != null ? qty : i.quantity + step } : i);
      }
      return [...prev, { catalog_product_id: product.id, name: product.name, image_url: product.image_url, unified_price: product.unified_price, unit: product.unit, quantity: qty || step }];
    });
  };

  const updateCartQty = (id, delta) => {
    setCart(prev => prev.map(i => {
      if (i.catalog_product_id === id) {
        const step = i.unit === 'kg' ? 0.5 : 1;
        const q = Math.round((i.quantity + delta * step) * 10) / 10;
        return q <= 0 ? null : { ...i, quantity: q };
      }
      return i;
    }).filter(Boolean));
  };

  const removeFromCart = (id) => setCart(prev => prev.filter(i => i.catalog_product_id !== id));
  const cartTotal = cart.reduce((s, i) => s + i.unified_price * i.quantity, 0);
  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const getCartQty = (pid) => cart.find(i => i.catalog_product_id === pid)?.quantity || 0;

  const checkActiveOrder = () => {
    const savedOrderId = localStorage.getItem('super_active_order');
    if (savedOrderId) setActiveOrderId(savedOrderId);
  };

  const goToCheckout = () => {
    if (!cart.length) { toast.error('Agrega productos a tu lista'); return; }
    navigate('/super/checkout');
  };

  // Saved Lists
  const saveCurrentList = () => {
    if (!cart.length) { toast.error('Tu lista está vacía'); return; }
    const name = prompt('Nombre de tu lista (ej: "Semanal", "Básicos")');
    if (!name) return;
    setSavedLists(prev => [...prev, { id: Date.now(), name, items: [...cart], created_at: new Date().toISOString() }]);
    toast.success(`Lista "${name}" guardada`);
  };

  const loadSavedList = (list) => {
    setCart(list.items);
    setShowSavedLists(false);
    toast.success(`Lista "${list.name}" cargada`);
  };

  const deleteSavedList = (id) => {
    setSavedLists(prev => prev.filter(l => l.id !== id));
  };

  // Recipes wizard
  const openRecipeWizard = async () => {
    setRecipeStep(0);
    setRecipeServings(4);
    setRecipeCategory(null);
    setRecipes([]);
    setShowRecipes(true);
    try {
      const res = await api.get('/super-inteligente/recipe-categories');
      setRecipeCategories(res.data);
    } catch (e) { console.error(e); }
  };

  const loadRecipesByCategory = async (cat) => {
    setRecipeCategory(cat);
    setRecipesLoading(true);
    setRecipeStep(2);
    try {
      const res = await api.get('/super-inteligente/recipes', { params: { category: cat, servings: recipeServings } });
      setRecipes(res.data);
    } catch (e) { console.error(e); }
    finally { setRecipesLoading(false); }
  };

  const addRecipeToCart = (recipe) => {
    recipe.ingredients.forEach(ing => {
      if (ing.available && ing.catalog_product_id) {
        const existing = cart.find(i => i.catalog_product_id === ing.catalog_product_id);
        if (!existing) {
          setCart(prev => [...prev, {
            catalog_product_id: ing.catalog_product_id,
            name: ing.name,
            image_url: ing.image_url,
            unified_price: ing.unified_price || 0,
            unit: ing.unit,
            quantity: ing.qty,
          }]);
        }
      }
    });
    setShowRecipes(false);
    toast.success(`Ingredientes de "${recipe.name}" agregados`);
  };

  // Barcode Scanner
  const startScanner = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      setShowScanner(true);
      setTimeout(() => {
        if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play(); }
      }, 100);
    } catch (e) {
      toast.error('No se pudo acceder a la cámara');
    }
  };

  const stopScanner = () => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
    }
    setShowScanner(false);
  };

  const handleBarcodeInput = async (barcode) => {
    stopScanner();
    setSearchQuery(barcode);
    try {
      const res = await api.get('/super-inteligente/products', { params: { barcode, limit: 1 } });
      if (res.data.products.length > 0) {
        const p = res.data.products[0];
        addToCart(p);
        toast.success(`${p.name} agregado`);
      } else {
        toast.error('Producto no encontrado');
      }
    } catch (e) { toast.error('Error al buscar'); }
  };

  // Savings calculator
  const estimatedSavings = {
    gas: cart.length > 0 ? Math.round(cart.length * 3.5) : 0, // ~$3.50 gas per product trip
    time: cart.length > 0 ? Math.round(cart.length * 2.5) : 0, // ~2.5 min per product
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Super Inteligente | Tu súper a domicilio - enLocal Puerto Vallarta</title>
        <meta name="description" content="Arma tu lista del súper y nuestro algoritmo encuentra los mejores precios en tiendas cercanas. Abarrotes, frutas, verduras, carnes y más. Un solo pedido, un solo envío." />
        <meta property="og:title" content="Super Inteligente | Tu súper sin ir al súper" />
        <meta property="og:description" content="Arma tu lista, nosotros compramos en las mejores tiendas de tu colonia y te lo llevamos. Apoya al comercio local." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://todoenlocal.com/super" />
        <meta property="og:image" content="https://todoenlocal.com/logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Super Inteligente | enLocal" />
        <meta name="twitter:description" content="Tu súper a domicilio. Un algoritmo inteligente compra por ti en las mejores tiendas locales." />
        <link rel="canonical" href="https://todoenlocal.com/super" />
      </Helmet>
      <MarketplaceHeader />

      {/* ==================== HERO ==================== */}
      <div className="relative overflow-hidden bg-[#0a1628]">
        <div className="absolute inset-0 opacity-40">
          <div className="absolute top-0 left-0 w-80 h-80 bg-emerald-500 rounded-full mix-blend-multiply filter blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-teal-400 rounded-full mix-blend-multiply filter blur-3xl animate-pulse" style={{ animationDelay: '1.5s' }} />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 py-12 md:py-16">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-emerald-500/20 backdrop-blur-sm text-emerald-300 px-4 py-1.5 rounded-full text-sm font-medium mb-5">
              <Zap className="w-4 h-4" /> Algoritmo inteligente
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-white leading-tight mb-4">
              Super<span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent"> Inteligente</span>
            </h1>
            <p className="text-white/60 text-lg mb-8">
              Arma tu lista del súper y nuestro algoritmo encuentra los mejores precios en tiendas cercanas.
            </p>

            {/* Search with autocomplete */}
            <div className="relative max-w-xl">
              <form onSubmit={e => { e.preventDefault(); handleSearch(); setShowSuggestions(false); }}>
                <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
                <input
                  ref={searchRef}
                  type="text"
                  placeholder="Buscar producto, marca o código de barras..."
                  value={searchQuery}
                  onChange={e => { setSearchQuery(e.target.value); setShowSuggestions(true); }}
                  onFocus={() => setShowSuggestions(true)}
                  className="w-full pl-14 pr-24 py-5 rounded-xl text-gray-900 text-base bg-white shadow-2xl shadow-black/20 focus:ring-4 focus:ring-emerald-500/30 focus:outline-none"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex gap-1">
                  {searchQuery && (
                    <button type="button" onClick={clearSearch} className="bg-gray-100 p-1.5 rounded-full">
                      <X className="w-4 h-4 text-gray-500" />
                    </button>
                  )}
                  <button type="button" onClick={startScanner} className="bg-emerald-100 p-1.5 rounded-full hover:bg-emerald-200" title="Escanear código de barras">
                    <Camera className="w-4 h-4 text-emerald-700" />
                  </button>
                </div>
              </form>

              {/* Autocomplete dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute top-full mt-2 w-full bg-white rounded-xl shadow-2xl border border-gray-100 z-50 max-h-72 overflow-y-auto">
                  {suggestions.map(s => (
                    <button key={s.id} onClick={() => selectSuggestion(s)}
                      className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 text-left transition">
                      {s.image_url && <img src={s.image_url} alt="" className="w-9 h-9 rounded-lg object-cover" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{s.name}</p>
                        <p className="text-xs text-gray-400 capitalize">{s.category?.replace('_', ' ')}</p>
                      </div>
                      <span className="text-sm font-bold text-emerald-600">${s.unified_price?.toFixed(2)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Quick action buttons */}
            <div className="flex gap-2 mt-4">
              <button onClick={openRecipeWizard} className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white/80 px-4 py-2 rounded-lg text-sm font-medium transition">
                <BookOpen className="w-4 h-4" /> Recetas
              </button>
              <button onClick={() => setShowSavedLists(true)} className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white/80 px-4 py-2 rounded-lg text-sm font-medium transition">
                <List className="w-4 h-4" /> Mis listas
              </button>
              {cart.length > 0 && (
                <button onClick={saveCurrentList} className="flex items-center gap-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 backdrop-blur-sm text-emerald-300 px-4 py-2 rounded-lg text-sm font-medium transition">
                  <Save className="w-4 h-4" /> Guardar lista
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ==================== ACTIVE ORDER BANNER ==================== */}
      {activeOrderId && (
        <div className="max-w-6xl mx-auto px-4 -mt-5 relative z-20 mb-2">
          <button onClick={() => navigate(`/super/pedido/${activeOrderId}`)}
            className="w-full flex items-center justify-between bg-emerald-600 text-white px-5 py-4 rounded-xl shadow-lg hover:bg-emerald-700 transition">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center animate-pulse">
                <Truck className="w-5 h-5" />
              </div>
              <div className="text-left">
                <p className="font-bold">Tienes un pedido activo</p>
                <p className="text-xs text-emerald-200">Toca para ver el seguimiento</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* ==================== HOW IT WORKS ==================== */}
      <div className="max-w-6xl mx-auto px-4 -mt-5 relative z-10 mb-2">
        <div className="grid grid-cols-3 gap-3">
          {[
            { icon: <ShoppingCart className="w-5 h-5" />, title: 'Arma tu lista', desc: 'Agrega lo que necesitas', color: 'text-emerald-600 bg-emerald-100' },
            { icon: <Store className="w-5 h-5" />, title: 'Buscamos por ti', desc: 'Mejores tiendas cercanas', color: 'text-teal-600 bg-teal-100' },
            { icon: <Truck className="w-5 h-5" />, title: 'Te lo llevamos', desc: 'Un solo envío a tu puerta', color: 'text-blue-600 bg-blue-100' },
          ].map((s, i) => (
            <div key={i} className="flex items-center gap-3 bg-white rounded-xl p-3 md:p-4 border border-gray-100 shadow-sm">
              <div className={`w-9 h-9 md:w-10 md:h-10 rounded-lg ${s.color} flex items-center justify-center flex-shrink-0`}>{s.icon}</div>
              <div className="hidden md:block">
                <p className="font-bold text-gray-900 text-sm">{s.title}</p>
                <p className="text-xs text-gray-500">{s.desc}</p>
              </div>
              <p className="md:hidden font-bold text-gray-900 text-xs leading-tight">{s.title}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ==================== SAVINGS TRACKER (when cart has items) ==================== */}
      {cart.length > 0 && (
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <DollarSign className="w-8 h-8 text-emerald-600" />
              <div>
                <p className="text-sm font-bold text-emerald-900">Ahorro estimado vs ir al súper</p>
                <p className="text-xs text-emerald-700">~${estimatedSavings.gas} en gasolina · ~{estimatedSavings.time} min de tu tiempo</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-lg font-extrabold text-emerald-600">${(estimatedSavings.gas).toFixed(0)}</p>
              <p className="text-[10px] text-emerald-500">ahorras</p>
            </div>
          </div>
        </div>
      )}

      {/* ==================== STICKY CATEGORY NAV ==================== */}
      {!searchResults && orderedSections.length > 0 && (
        <div className="sticky top-16 z-30 bg-gray-50/95 backdrop-blur-sm border-b border-gray-200 shadow-sm">
          <div className="max-w-6xl mx-auto px-4">
            <div ref={navRef} className="flex gap-1 overflow-x-auto py-2.5 scrollbar-hide">
              {orderedSections.map(cat => {
                const meta = SECTIONS_META[cat] || { label: cat };
                return (
                  <button key={cat} id={`nav-${cat}`} onClick={() => scrollToSection(cat)}
                    className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
                      activeSection === cat ? 'bg-emerald-600 text-white shadow-md' : 'text-gray-600 hover:bg-white hover:shadow-sm'
                    }`}
                  >{meta.label}</button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ==================== SECTIONS WITH PRODUCTS ==================== */}
      <div className="max-w-6xl mx-auto px-4 py-6 pb-44 space-y-10">
        {loading ? (
          <div className="space-y-8">
            {[...Array(3)].map((_, i) => (
              <div key={i}>
                <div className="h-32 bg-gray-200 rounded-2xl animate-pulse mb-4" />
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {[...Array(5)].map((_, j) => (
                    <div key={j} className="bg-white rounded-xl border border-gray-100 overflow-hidden animate-pulse">
                      <div className="aspect-square bg-gray-200" />
                      <div className="p-3 space-y-2"><div className="h-4 bg-gray-200 rounded w-3/4" /><div className="h-5 bg-gray-100 rounded w-1/2" /></div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : searchResults ? (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-extrabold text-gray-900">Resultados para "{searchQuery}"</h2>
              <button onClick={clearSearch} className="text-sm text-emerald-600 font-medium hover:underline">Limpiar</button>
            </div>
            {searchResults.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
                <Package className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                <p className="text-gray-900 text-lg font-bold">No se encontraron productos</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {searchResults.map(p => <ProductCard key={p.id} product={p} cartQty={getCartQty(p.id)} onAdd={() => addToCart(p)} onQtyChange={(d) => updateCartQty(p.id, d)} onClick={() => setSelectedProduct(p)} />)}
              </div>
            )}
          </div>
        ) : (
          orderedSections.map(cat => {
            const meta = SECTIONS_META[cat] || { label: cat, img: null, desc: '' };
            const prods = productsByCategory[cat] || [];
            return (
              <div key={cat} ref={el => { sectionRefs.current[cat] = el; }}>
                <div className="relative h-28 md:h-36 rounded-2xl overflow-hidden mb-5 group">
                  {meta.img ? (
                    <img src={meta.img} alt={meta.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-r from-emerald-600 to-teal-500" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
                  <div className="absolute inset-0 flex items-center px-6 md:px-8">
                    <div>
                      <h2 className="text-2xl md:text-3xl font-black text-white drop-shadow-lg">{meta.label}</h2>
                      <p className="text-white/70 text-sm mt-1">{meta.desc}</p>
                    </div>
                  </div>
                  <div className="absolute top-3 right-3 bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-lg text-sm font-medium">
                    {prods.length} productos
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {prods.map(p => <ProductCard key={p.id} product={p} cartQty={getCartQty(p.id)} onAdd={() => addToCart(p)} onQtyChange={(d) => updateCartQty(p.id, d)} onClick={() => setSelectedProduct(p)} />)}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* ==================== FLOATING CART BAR ==================== */}
      {cart.length > 0 && (
        <div className="fixed bottom-16 md:bottom-0 left-0 right-0 z-40 p-4">
          <div className="max-w-xl mx-auto">
            <button onClick={() => setShowCart(true)}
              className="w-full flex items-center justify-between bg-emerald-600 text-white px-6 py-4 rounded-2xl shadow-2xl shadow-emerald-600/30 hover:bg-emerald-700 transition-all">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 rounded-xl p-2 relative">
                  <ShoppingCart className="w-5 h-5" />
                  <span className="absolute -top-1.5 -right-1.5 bg-white text-emerald-700 text-[10px] font-black rounded-full w-5 h-5 flex items-center justify-center shadow">{cart.length}</span>
                </div>
                <div className="text-left">
                  <p className="font-bold">Mi lista del súper</p>
                  <p className="text-xs text-emerald-200">{cart.length} productos</p>
                </div>
              </div>
              <span className="text-xl font-extrabold">${cartTotal.toFixed(2)}</span>
            </button>
          </div>
        </div>
      )}

      {/* ==================== CART DRAWER ==================== */}
      {showCart && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center" onClick={() => setShowCart(false)}>
          <div className="bg-white w-full md:max-w-lg md:rounded-2xl rounded-t-3xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div>
                <h2 className="text-xl font-extrabold text-gray-900">Mi lista del súper</h2>
                <p className="text-sm text-gray-500">{cart.length} productos</p>
              </div>
              <div className="flex gap-2">
                <button onClick={saveCurrentList} className="p-2 hover:bg-gray-100 rounded-xl" title="Guardar lista"><Save className="w-5 h-5 text-emerald-600" /></button>
                <button onClick={() => setShowCart(false)} className="p-2 hover:bg-gray-100 rounded-xl"><X className="w-5 h-5" /></button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {cart.map(item => (
                <div key={item.catalog_product_id} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                  {item.image_url ? (
                    <img src={item.image_url} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-14 h-14 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0"><Package className="w-6 h-6 text-gray-400" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{item.name}</p>
                    <p className="text-sm text-emerald-600 font-bold">${(item.unified_price * item.quantity).toFixed(2)}</p>
                  </div>
                  <div className="flex items-center gap-1 bg-white rounded-lg border border-gray-200">
                    <button onClick={() => updateCartQty(item.catalog_product_id, -1)} className="p-1.5"><Minus className="w-3.5 h-3.5 text-gray-600" /></button>
                    <span className="text-xs font-bold w-8 text-center">{item.unit === 'kg' ? `${item.quantity}kg` : item.quantity}</span>
                    <button onClick={() => updateCartQty(item.catalog_product_id, 1)} className="p-1.5"><Plus className="w-3.5 h-3.5 text-gray-600" /></button>
                  </div>
                  <button onClick={() => removeFromCart(item.catalog_product_id)} className="p-1.5 text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>

            <div className="border-t border-gray-100 p-5 space-y-3">
              {/* Community impact */}
              <div className="flex items-center gap-2 bg-amber-50 rounded-lg p-3">
                <Store className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <p className="text-xs text-amber-800 font-medium">Tu compra apoya directamente a tiendas de tu colonia</p>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Subtotal</span>
                <span className="text-xl font-extrabold text-gray-900">${cartTotal.toFixed(2)}</span>
              </div>
              <p className="text-xs text-gray-400">El costo de envío se calcula al confirmar tu dirección de entrega.</p>
              <button onClick={goToCheckout} className="w-full bg-emerald-600 text-white py-4 rounded-xl font-bold text-lg hover:bg-emerald-700 transition shadow-lg flex items-center justify-center gap-2">
                Continuar al pago <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== PRODUCT DETAIL SHEET ==================== */}
      {selectedProduct && (
        <SuperProductSheet
          product={selectedProduct}
          cartQty={getCartQty(selectedProduct.id)}
          onAdd={() => addToCart(selectedProduct)}
          onQtyChange={(d) => updateCartQty(selectedProduct.id, d)}
          onClose={() => setSelectedProduct(null)}
        />
      )}

      {/* ==================== RECIPES WIZARD ==================== */}
      {showRecipes && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowRecipes(false)}>
          <div className="bg-white w-full md:max-w-lg rounded-2xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                {recipeStep > 0 && (
                  <button onClick={() => setRecipeStep(recipeStep - 1)} className="p-1 hover:bg-gray-100 rounded-lg">
                    <ChevronRight className="w-5 h-5 rotate-180 text-gray-500" />
                  </button>
                )}
                <div>
                  <h2 className="text-xl font-extrabold text-gray-900">
                    {recipeStep === 0 ? '¿Para cuántas personas?' : recipeStep === 1 ? '¿Qué se te antoja?' : 'Elige tu receta'}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {recipeStep === 0 ? 'Ajustaremos las cantidades' : recipeStep === 1 ? 'Selecciona un tipo de cocina' : `${recipes.length} opciones para ${recipeServings} personas`}
                  </p>
                </div>
              </div>
              <button onClick={() => setShowRecipes(false)} className="p-2 hover:bg-gray-100 rounded-xl"><X className="w-5 h-5" /></button>
            </div>

            {/* Progress */}
            <div className="flex gap-1.5 px-5 pt-3">
              {[0,1,2].map(i => (
                <div key={i} className={`flex-1 h-1 rounded-full ${i <= recipeStep ? 'bg-emerald-500' : 'bg-gray-200'}`} />
              ))}
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {/* Step 0: Personas */}
              {recipeStep === 0 && (
                <div className="space-y-6">
                  <div className="text-center py-4">
                    <span className="text-6xl">👨‍👩‍👧‍👦</span>
                  </div>
                  {/* Quick select */}
                  <div className="grid grid-cols-5 gap-3">
                    {[1,2,3,4,5,6,8,10,15,20].map(n => (
                      <button key={n} onClick={() => setRecipeServings(n)}
                        className={`aspect-square rounded-2xl flex flex-col items-center justify-center font-bold text-lg transition-all ${
                          recipeServings === n ? 'bg-emerald-600 text-white shadow-lg scale-105' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                  {/* Custom input */}
                  <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                    <span className="text-sm text-gray-500">Otro:</span>
                    <input type="number" min="1" max="100" value={recipeServings}
                      onChange={e => setRecipeServings(Math.max(1, parseInt(e.target.value) || 1))}
                      className="flex-1 bg-white border border-gray-200 rounded-lg px-3 py-2 text-center font-bold text-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                    <span className="text-sm text-gray-500">personas</span>
                  </div>
                  <button onClick={() => setRecipeStep(1)} className="w-full bg-emerald-600 text-white py-4 rounded-xl font-bold text-lg hover:bg-emerald-700 transition">
                    Siguiente
                  </button>
                </div>
              )}

              {/* Step 1: Antojo */}
              {recipeStep === 1 && (
                <div className="grid grid-cols-3 gap-3">
                  {recipeCategories.map(cat => (
                    <button key={cat.id} onClick={() => loadRecipesByCategory(cat.id)}
                      className="flex flex-col items-center gap-2 p-4 bg-gray-50 rounded-2xl hover:bg-emerald-50 hover:shadow-md transition-all group">
                      <span className="text-4xl group-hover:scale-110 transition-transform">{cat.emoji}</span>
                      <span className="text-sm font-semibold text-gray-900 text-center leading-tight">{cat.label}</span>
                      <span className="text-[10px] text-gray-400">{cat.count} recetas</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Step 2: Resultados */}
              {recipeStep === 2 && (
                <div className="space-y-3">
                  {recipesLoading ? (
                    <div className="flex justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                    </div>
                  ) : recipes.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-gray-500">No se encontraron recetas</p>
                    </div>
                  ) : recipes.map(r => (
                    <div key={r.id} className="bg-gray-50 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <span className="text-3xl">{r.image}</span>
                          <div>
                            <p className="font-bold text-gray-900">{r.name}</p>
                            <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                              <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{r.time}</span>
                              <span className="flex items-center gap-1"><Users className="w-3 h-3" />{r.servings} pers.</span>
                              <span className="px-1.5 py-0.5 bg-gray-200 rounded text-[10px]">{r.difficulty}</span>
                            </div>
                          </div>
                        </div>
                        <p className="text-lg font-extrabold text-emerald-600">${r.estimated_cost?.toFixed(0)}</p>
                      </div>
                      <div className="space-y-1 mb-3">
                        {r.ingredients.map((ing, j) => (
                          <div key={j} className="flex items-center justify-between text-sm">
                            <span className={ing.available ? 'text-gray-700' : 'text-gray-400 line-through'}>{ing.name}</span>
                            <span className="text-gray-500">{ing.qty} {ing.unit}</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">{r.matched}/{r.total_ingredients} disponibles</span>
                        <button onClick={() => addRecipeToCart(r)} className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-emerald-700 transition">
                          Agregar ingredientes
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ==================== SAVED LISTS MODAL ==================== */}
      {showSavedLists && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center" onClick={() => setShowSavedLists(false)}>
          <div className="bg-white w-full md:max-w-lg md:rounded-2xl rounded-t-3xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="text-xl font-extrabold text-gray-900">Mis listas guardadas</h2>
              <button onClick={() => setShowSavedLists(false)} className="p-2 hover:bg-gray-100 rounded-xl"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {savedLists.length === 0 ? (
                <div className="text-center py-12">
                  <List className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                  <p className="text-gray-500">No tienes listas guardadas</p>
                  <p className="text-xs text-gray-400 mt-1">Arma tu lista y guárdala para reordenar rápido</p>
                </div>
              ) : (
                savedLists.map(list => (
                  <div key={list.id} className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-bold text-gray-900">{list.name}</p>
                        <p className="text-xs text-gray-500">{list.items.length} productos · {new Date(list.created_at).toLocaleDateString('es-MX')}</p>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => loadSavedList(list)} className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-emerald-700">
                          Cargar
                        </button>
                        <button onClick={() => deleteSavedList(list.id)} className="p-2 text-gray-400 hover:text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="flex gap-1.5 overflow-hidden">
                      {list.items.slice(0, 5).map((item, j) => (
                        item.image_url ? <img key={j} src={item.image_url} alt="" className="w-8 h-8 rounded object-cover" /> :
                        <div key={j} className="w-8 h-8 rounded bg-gray-200 flex items-center justify-center"><Package className="w-4 h-4 text-gray-400" /></div>
                      ))}
                      {list.items.length > 5 && <span className="text-xs text-gray-400 self-center ml-1">+{list.items.length - 5}</span>}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* ==================== BARCODE SCANNER MODAL ==================== */}
      {showScanner && (
        <div className="fixed inset-0 bg-black z-50 flex flex-col">
          <div className="flex items-center justify-between p-4">
            <h2 className="text-white font-bold text-lg">Escanear código de barras</h2>
            <button onClick={stopScanner} className="bg-white/20 p-2 rounded-full"><X className="w-5 h-5 text-white" /></button>
          </div>
          <div className="flex-1 flex items-center justify-center">
            <video ref={videoRef} className="w-full max-w-md rounded-xl" playsInline />
          </div>
          <div className="p-4">
            <p className="text-white/60 text-center text-sm mb-3">O ingresa el código manualmente:</p>
            <form onSubmit={e => { e.preventDefault(); const v = e.target.barcode.value; if (v) handleBarcodeInput(v); }} className="flex gap-2">
              <input name="barcode" type="text" inputMode="numeric" placeholder="Código de barras" className="flex-1 px-4 py-3 rounded-xl text-white bg-white/10 border border-white/20 placeholder-white/40" />
              <button type="submit" className="bg-emerald-600 text-white px-6 py-3 rounded-xl font-bold">Buscar</button>
            </form>
          </div>
        </div>
      )}

      <MarketplaceFooter />
      <MobileBottomNav />
    </div>
  );
};


// ==================== PRODUCT CARD ====================

const ProductCard = ({ product: p, cartQty, onAdd, onQtyChange, onClick }) => (
  <div className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-lg transition-all duration-200 group">
    <button onClick={onClick} className="w-full text-left">
      <div className="relative aspect-square bg-gray-50">
        {p.image_url ? (
          <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><Package className="w-12 h-12 text-gray-200" /></div>
        )}
        {p.unit === 'kg' && (
          <span className="absolute top-2 right-2 text-[10px] font-bold text-gray-600 bg-white/90 px-2 py-0.5 rounded-md shadow-sm">por kg</span>
        )}
      </div>
      <div className="p-3 pb-0">
        <p className="text-sm font-semibold text-gray-900 leading-tight line-clamp-2 min-h-[2.5rem] group-hover:text-emerald-600 transition-colors">{p.name}</p>
      </div>
    </button>
    <div className="p-3 pt-0">
      <div className="flex items-end justify-between mt-2">
        <p className="text-lg font-extrabold text-emerald-600">${p.unified_price.toFixed(2)}</p>
        {cartQty === 0 ? (
          <button onClick={onAdd} className="bg-emerald-600 text-white p-2 rounded-lg hover:bg-emerald-700 transition shadow-sm">
            <Plus className="w-5 h-5" />
          </button>
        ) : (
          <div className="flex items-center gap-1 bg-emerald-50 rounded-lg border border-emerald-200">
            <button onClick={() => onQtyChange(-1)} className="p-1.5 hover:bg-emerald-100 rounded-l-lg transition"><Minus className="w-4 h-4 text-emerald-700" /></button>
            <span className="text-xs font-bold text-emerald-700 w-8 text-center">{p.unit === 'kg' ? `${cartQty}kg` : cartQty}</span>
            <button onClick={() => onQtyChange(1)} className="p-1.5 hover:bg-emerald-100 rounded-r-lg transition"><Plus className="w-4 h-4 text-emerald-700" /></button>
          </div>
        )}
      </div>
    </div>
  </div>
);


export default SuperInteligente;
